/**
 * Onboarding branding.
 *
 * The redesign moves to Montserrat for headings and keeps PT Sans for body
 * copy, on a palette taken from the design's CSS custom properties. Scoped to
 * this feature rather than applied to the app theme: the rest of the product
 * has not been redesigned yet, and changing the global theme would restyle
 * every existing screen as a side effect of an onboarding ticket.
 *
 * When the redesign reaches the rest of the app, these tokens move up into the
 * root theme in `src/App.jsx` and this module goes away.
 *
 * Values mirror `src/styles/theme.css` in the Figma Make export.
 *
 * ## Naming
 *
 * A token names a **colour**; the palette below names a **role**. The export
 * uses shadcn's vocabulary, where `--primary` is the navy and `--accent` the
 * teal; MUI's palette uses `primary`/`secondary` for its own two slots. Reusing
 * either word here would mean one name for two colours, so the brand pair is
 * named for what it looks like and the palette says what each one is for.
 *
 * Tokens that describe a role rather than a colour — `background`, `card`,
 * `muted`, `track` — keep their role names, because that is genuinely what they
 * are.
 *
 * There is deliberately no `foreground` alias for `navy` and no `white`: one
 * colour with two token names is how the two drift apart. Text on the brand
 * colour reads `primary.contrastText` from the palette instead.
 */

export const onboardingTokens = {
  /** Brand pair. */
  navy: "#012f7c",
  teal: "#01becc",

  /** A pale tint of the teal, used behind small icons and badges. */
  lightTeal: "#e0f0f7",

  background: "#f3f7f8",
  card: "#ffffff",
  muted: "#ddeaf0",
  mutedForeground: "#4a7ba7",
  destructive: "#d4183d",

  /**
   * The export's `--border`, `oklch(0.42 0.09 240 / 0.18)`, as navy at 18%.
   * Kept in the same colour space as `track` below rather than introducing a
   * second one for a single value.
   */
  border: "rgba(1, 47, 124, 0.18)",

  /** The progress bar's unfilled portion. */
  track: "rgba(1, 47, 124, 0.14)",

  radius: "0.625rem",

  fontHeading: '"Montserrat", "PT Sans", sans-serif',
  fontBody: '"PT Sans", sans-serif',
};

/**
 * `sx` for text read by assistive technology but not shown. Pixel strings
 * because `sx` reads unitless `1` as 100%.
 */
export const visuallyHidden = {
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  padding: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

/**
 * Theme overrides, applied on top of the app theme by the onboarding layout.
 *
 * Deliberately a plain options object rather than a finished theme: the layout
 * passes it to `createTheme(appTheme, onboardingThemeOverrides)`, so MUI does
 * the deep merge. Hand-spreading a theme looks equivalent and is not — it drops
 * nested defaults the components rely on, and does it silently.
 */
export const onboardingThemeOverrides = {
  palette: {
    background: {
      default: onboardingTokens.background,
      paper: onboardingTokens.card,
    },
    /**
     * Teal and Navy are the brand colours.
     * Teal is the action colour: contained buttons, the focused input outline,
     * the focused label and checked checkboxes all read `primary` without being
     * told to.
     *
     * MUI's `createTheme()` derives `light` and `dark`,
     * which is what gives contained buttons their hover and active states.
     *
     * White on teal is 2.27:1, below WCAG AA's 4.5:1. It ships this way to
     * match the design; navy on teal is 5.44:1 and is a one-line change to
     * `contrastText` alone, with no component touched.
     */
    primary: {
      main: onboardingTokens.teal,
      contrastText: "#ffffff",
    },
    /** Navy is the chrome: the back control and the progress fill. */
    secondary: {
      main: onboardingTokens.navy,
    },
    error: {
      main: onboardingTokens.destructive,
    },
    text: {
      primary: onboardingTokens.navy,
      secondary: onboardingTokens.mutedForeground,
    },
    /**
     * MUI's own disabled greys belong to a different palette. Set here rather
     * than on the button so every disabled control agrees.
     */
    action: {
      disabled: onboardingTokens.mutedForeground,
      disabledBackground: onboardingTokens.muted,
    },
  },
  typography: {
    fontFamily: onboardingTokens.fontBody,

    /**
     * Sizes live here so a heading does not depend on every screen remembering
     * to override MUI's defaults, which start at 6rem for `h1`. Screens that
     * want a different size — the welcome hero and the completion screen — say
     * so deliberately in their own `sx`.
     */
    h1: {
      fontFamily: onboardingTokens.fontHeading,
      fontWeight: 700,
      fontSize: "clamp(1.5rem, 4vw, 2rem)",
    },
    h2: {
      fontFamily: onboardingTokens.fontHeading,
      fontWeight: 700,
      fontSize: "1.25rem",
    },
    h3: {
      fontFamily: onboardingTokens.fontHeading,
      fontWeight: 700,
      fontSize: "1.125rem",
    },
    h4: {
      fontFamily: onboardingTokens.fontHeading,
      fontWeight: 600,
      fontSize: "1rem",
    },
    h5: { fontFamily: onboardingTokens.fontHeading, fontWeight: 600 },
    h6: { fontFamily: onboardingTokens.fontHeading, fontWeight: 600 },
  },
  components: {
    // `src/index.jsx` sets 1.2rem vertical padding and a 40px radius on every
    // MuiButtonBase. Those never reach the app: nested MUI ThemeProviders merge
    // shallowly ({...outer, ...local}), so App.jsx's theme replaces the
    // `components` key wholesale. No reset is needed here, and adding one would
    // imply an inheritance that does not exist.
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          // The onboarding flow must keep keyboard focus visible even when
          // browser defaults are reset by the surrounding application.
          "&:focus-visible": {
            outline: `3px solid ${onboardingTokens.navy}`,
            outlineOffset: 2,
          },
        },
      },
    },
    /**
     * Typography inherits its colour and there is nothing above it to inherit
     * from: the app never renders `<CssBaseline />`, so `body` carries no colour
     * rule and text falls back to the browser's black. `Paper` is the only MUI
     * component that applies `text.primary` itself, which is why dialogs already
     * read navy while the step screens did not.
     *
     * Set here rather than as a `color` on the layout's wrapper, because
     * `Dialog` portals out of that subtree — a CSS colour there would never
     * reach the age gate or the policy viewer. Body copy that asks for
     * `mutedForeground` is unaffected: `sx` and the `color` prop both resolve
     * after `styleOverrides`.
     */
    MuiTypography: {
      styleOverrides: {
        root: {
          color: onboardingTokens.navy,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: onboardingTokens.radius,
          fontFamily: onboardingTokens.fontBody,
          fontSize: "1rem",
          fontWeight: 600,
          lineHeight: 1.5,
          paddingTop: "0.75rem",
          paddingBottom: "0.75rem",
        },
        // Nothing for `contained` — `palette.primary` drives its fill, label and
        // derived hover, and `palette.action` its disabled state.
        //
        // `outlined` cannot come from the palette: the design's secondary button
        // is muted grey on transparent inside a neutral border, which is not any
        // palette colour.
        outlined: {
          color: onboardingTokens.mutedForeground,
          borderColor: onboardingTokens.border,
          borderWidth: "1.5px",
          fontSize: "0.9375rem",
          fontWeight: 500,
          // Restated, or the border thins by half a pixel on hover and the
          // button appears to shift.
          "&:hover": {
            borderColor: onboardingTokens.border,
            borderWidth: "1.5px",
          },
        },
      },
    },
    /**
     * MUI's outlined variant is transparent by default, so the field disappears
     * into the page. Filled white, per the design's date and account screens.
     *
     * The export is not self-consistent here: its shared `ui/input.tsx` fills
     * with `--input-background` (#e8f2f6) while `DOBStep.tsx` fills its own
     * fields with `--card`. White is what the screens actually show.
     *
     * Restated for hover and focus, which otherwise paint over the fill.
     */
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: onboardingTokens.card,
          "&:hover, &.Mui-focused": {
            backgroundColor: onboardingTokens.card,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: onboardingTokens.navy,
          fontWeight: 600,
        },
      },
    },
  },
};
